import axios, { AxiosInstance as AxiosInstanceType, AxiosRequestConfig } from 'axios';
import { Store, ActionCreator } from 'redux';
import { Action } from 'deox';

import { changeDataKeysCase, camelToSnakeCase } from 'utils';
import { Store as AppStore } from 'types';

import { API_URL } from './constants';

type AxiosInstance = {
  instance: AxiosInstanceType | null;
  refreshingAccessToken: boolean;
  refreshAccessTokenUrl: string;
  requestsConfigsQueue: Promise<AxiosRequestConfig>[];
  create: (
    store: Store,
    updateTokensAction: ActionCreator<Action<string>>,
    actionOnUpdateTokensFail: ActionCreator<Action<string>>,
  ) => void;
};

export const axiosInstance: AxiosInstance = {
  instance: null,
  refreshingAccessToken: false,
  refreshAccessTokenUrl: 'token/refresh/',
  requestsConfigsQueue: [],
  create(store, updateTokensAction, actionOnUpdateTokensFail) {
    this.instance = axios.create({
      baseURL: API_URL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      transformRequest: (requestData, headers) => {
        if (headers['Content-Type'] === 'multipart/form-data') {
          if (requestData instanceof FormData) {
            return requestData;
          }

          const formData = new FormData();
          // eslint-disable-next-line guard-for-in, no-restricted-syntax
          for (const key in requestData) {
            if (Array.isArray(requestData[key])) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              requestData[key].forEach((arrayItem: any) => {
                formData.append(camelToSnakeCase(key), arrayItem);
              });
            } else {
              formData.append(camelToSnakeCase(key), requestData[key]);
            }
          }

          return formData;
        }

        return JSON.stringify(changeDataKeysCase(requestData, 'snakeCase'));
      },
      transformResponse: (responseJSON: string | null) => {
        if (responseJSON !== null) {
          const responseData = JSON.parse(responseJSON);
          return changeDataKeysCase(responseData, 'camelCase');
        }

        return undefined;
      },
      validateStatus: (status) => {
        return (status >= 200 && status < 300) || status === 401;
      },
    });

    this.instance.interceptors.request.use(
      (config) => {
        if (this.refreshingAccessToken && config.url !== this.refreshAccessTokenUrl) {
          const queuedRequestConfigPromise = new Promise<AxiosRequestConfig>((resolve) => {
            const appStore: AppStore = store.getState();
            const previousAccessToken = appStore.user.data?.access;

            const unsubscribeFromStore = store.subscribe(() => {
              const appStore: AppStore = store.getState();

              const updatedAccessToken = appStore.user.data?.access;

              if (previousAccessToken !== updatedAccessToken) {
                unsubscribeFromStore();

                config.headers.Authorization = `Bearer ${appStore.user.data?.access}`;
                resolve(config);
              }
            });
          });

          return queuedRequestConfigPromise;
        }

        const appStore: AppStore = store.getState();

        if (appStore.user) {
          config.headers.Authorization = `Bearer ${appStore.user.data?.access}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.instance.interceptors.response.use(
      async (response) => {
        if (response.status === 401) {
          if (response.config.url === this.refreshAccessTokenUrl) {
            store.dispatch(actionOnUpdateTokensFail());
            return Promise.reject();
          }

          if (this.refreshingAccessToken === false) {
            this.refreshingAccessToken = true;
            const appStore: AppStore = store.getState();
            const updateTokensResponse = await this.instance?.post(this.refreshAccessTokenUrl, {
              refresh: appStore.user.data?.refresh,
            });

            store.dispatch(updateTokensAction(updateTokensResponse?.data));
            this.refreshingAccessToken = false;
          }

          return this.instance!(response.config);
        }

        return response;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  },
};
